
import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    email: '',
    telefono: '',
    sector: '',
    producto: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6e6d26qd308q1a0ae80', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          empresa: '',
          email: '',
          telefono: '',
          sector: '',
          producto: '',
          mensaje: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Información de Contacto */}
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Contacto
            </h2>
            <p className="text-lg text-slate-600 mb-12 leading-relaxed">
              Nuestro equipo de ingeniería está listo para asesorarle en la selección de la solución más adecuada para su proyecto de infraestructura crítica
            </p>

            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-blue-900 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-mail-line text-xl text-white"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-500 mb-1">Email</div>
                  <a href="mailto:ventas@bigbattery.co" className="text-base font-semibold text-slate-900 hover:text-blue-900 transition-colors">
                    ventas@bigbattery.co
                  </a>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-blue-900 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-phone-line text-xl text-white"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-500 mb-1">Teléfono</div>
                  <a href="tel:+34936258148" className="text-base font-semibold text-slate-900 hover:text-blue-900 transition-colors">
                    +34 936 25 81 48
                  </a>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-blue-900 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-global-line text-xl text-white"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-500 mb-1">Web</div>
                  <a href="https://www.bigbattery.co" target="_blank" rel="noopener noreferrer" className="text-base font-semibold text-slate-900 hover:text-blue-900 transition-colors">
                    www.bigbattery.co
                  </a>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-blue-900 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-map-pin-line text-xl text-white"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-500 mb-1">Dirección</div>
                  <div className="text-base font-semibold text-slate-900">
                    Calle riu de l'or, 39<br />
                    08034, Barcelona, España
                  </div>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-blue-900 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-building-line text-xl text-white"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-500 mb-1">NIF</div>
                  <div className="text-base font-semibold text-slate-900">
                    B67614271
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Formulario */}
          <div className="bg-white rounded-2xl p-8 shadow-xl">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Solicitar Información Técnica
            </h3>
            
            <form id="contact_form" onSubmit={handleSubmit} data-readdy-form>
              <div className="space-y-5">
                <div>
                  <label htmlFor="nombre" className="block text-sm font-medium text-slate-700 mb-2">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="empresa" className="block text-sm font-medium text-slate-700 mb-2">
                    Empresa *
                  </label>
                  <input
                    type="text"
                    id="empresa"
                    name="empresa"
                    value={formData.empresa}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    Email corporativo *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="telefono" className="block text-sm font-medium text-slate-700 mb-2">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="sector" className="block text-sm font-medium text-slate-700 mb-2">
                    Sector *
                  </label>
                  <select
                    id="sector"
                    name="sector"
                    value={formData.sector}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
                  >
                    <option value="">Seleccione un sector</option>
                    <option value="Data Centers">Data Centers</option>
                    <option value="Telecomunicaciones">Telecomunicaciones</option>
                    <option value="Industria">Industria</option>
                    <option value="Energía">Energía</option>
                    <option value="Gobierno">Gobierno</option>
                    <option value="Infraestructura Crítica">Infraestructura Crítica</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="producto" className="block text-sm font-medium text-slate-700 mb-2">
                    Producto de interés
                  </label>
                  <select
                    id="producto"
                    name="producto"
                    value={formData.producto}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
                  >
                    <option value="">Seleccione un producto</option>
                    <option value="UPS Monofásico">UPS Monofásico</option>
                    <option value="UPS Trifásico">UPS Trifásico</option>
                    <option value="Rectificador">Rectificador</option>
                    <option value="Inversor">Inversor</option>
                    <option value="Baterías">Baterías</option>
                    <option value="Solución Completa">Solución Completa</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="mensaje" className="block text-sm font-medium text-slate-700 mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all resize-none"
                    placeholder="Describa su proyecto o necesidad técnica..."
                  ></textarea>
                  <div className="text-xs text-slate-500 mt-1">
                    {formData.mensaje.length}/500 caracteres
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-900 text-white py-4 rounded-lg text-base font-semibold hover:bg-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    ¡Gracias! Su solicitud ha sido enviada correctamente. Nos pondremos en contacto pronto.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    Ha ocurrido un error. Por favor, inténtelo de nuevo o contáctenos directamente.
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
